import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, Phone } from "lucide-react";
import Logo from "./Logo";
import { NAV_LINKS, SERVICES, COMPANY } from "../mock";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md border-b border-black/5 shadow-sm" : "bg-white/90 backdrop-blur-md border-b border-black/5"
      }`}
    >
      <div className="max-w-[1240px] mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between h-[84px]">
          <Logo />

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) =>
              link.hasDropdown ? (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <Link
                    to={link.to}
                    className={`flex items-center gap-1 text-[15px] font-medium transition-colors ${
                      isActive(link.to) ? "text-[#0b2545]" : "text-slate-600 hover:text-[#0b2545]"
                    }`}
                  >
                    {link.label}
                    <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                    {isActive(link.to) && <span className="absolute -bottom-1 left-0 w-6 h-[2px] bg-[#1e73ff]" />}
                  </Link>
                  <div
                    className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 w-[300px] transition-all duration-200 ${
                      servicesOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"
                    }`}
                  >
                    <div className="bg-white rounded-2xl shadow-xl border border-black/5 p-2">
                      {SERVICES.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/services/${s.slug}`}
                      className="block px-4 py-3 rounded-xl text-[14px] text-slate-700 hover:bg-slate-100 hover:text-[#0b2545] transition-colors"
                        >
                          {s.title}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={link.label}
                  to={link.to}
                  className={`relative text-[15px] font-medium transition-colors ${
                    isActive(link.to) ? "text-[#0b2545]" : "text-slate-600 hover:text-[#0b2545]"
                  }`}
                >
                  {link.label}
                  {isActive(link.to) && <span className="absolute -bottom-1 left-0 w-full h-[2px] bg-[#1e73ff]" />}
                </Link>
              )
            )}
          </nav>

          <div className="flex items-center gap-4">
            <a
              href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
              className="hidden md:inline-flex items-center gap-2 text-[14px] font-semibold text-[#0b2545] hover:text-[#1e73ff] transition-colors"
            >
              <Phone className="w-4 h-4 text-[#1e73ff]" /> {COMPANY.phone}
            </a>
            <Link
              to="/contact"
              className="hidden sm:inline-flex items-center rounded-full bg-[#1e73ff] text-white shadow-sm shadow-[#1e73ff]/25 text-[14px] font-semibold px-6 py-3 hover:bg-[#155ed6] transition-colors"
            >
              Request a Quote
            </Link>
            <button
              className="lg:hidden p-2 text-[#0b2545]"
              onClick={() => setMobileOpen((o) => !o)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-black/5 px-5 py-4">
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="py-3 text-[16px] font-medium text-slate-800 border-b border-black/5"
              >
                {link.label}
              </Link>
            ))}
            <a
              href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
              className="py-3 text-[16px] font-semibold text-[#0b2545] border-b border-black/5 flex items-center gap-2"
            >
              <Phone className="w-4 h-4 text-[#1e73ff]" /> {COMPANY.phone}
            </a>
            <Link
              to="/contact"
              className="mt-4 inline-flex justify-center rounded-full bg-[#1e73ff] text-white shadow-sm shadow-[#1e73ff]/25 text-[15px] font-semibold px-6 py-3"
            >
              Request a Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
